import captionService from "CaptionService";
import type { DateTimeFormatType } from "DateTimeConstants";
import { DeferredPromise } from "DeferredPromise";
import { getDependencyValueAsync } from "Dependency2";
import type { Disposable } from "Disposable";
import entityMappingService from "EntityMappingService";
import global from "Global";
import { NotificationType } from "NotificationType";
import { getNumberFormat, isValidDigitalGroupSpacingOption } from "NumericService";
import ruleRepository from "RuleRepository";
import toastService from "ToastService";
import userSession from "UserSession";
import { type DigitalGroupSpacingOption } from "autonumeric";
import ko from "knockout";
import { reactive } from "vue";
import { type NumericLocale } from "wtg-material-ui";

const defaultNumericLocale: NumericLocale = Object.freeze({
  decimalCharacter: ".",
  digitGroupSeparator: "",
  digitalGroupSpacing: "3",
});

const defaultTimeInfo: TimeInfo = Object.freeze({
  defaultTimeZone: undefined,
  dateTimeFormatConfig: undefined,
});

export const numericLocale: NumericLocale = reactive({
  ...defaultNumericLocale,
});

export const timeInfo: TimeInfo = reactive({
  ...defaultTimeInfo,
});

export interface TimeInfo {
  defaultTimeZone: string | undefined;
  dateTimeFormatConfig: LocaleDateTimeFormatConfig | undefined;
}

export interface LocaleDateTimeFormatConfig {
  getParseDateFormats?: (dateTimeType: DateTimeFormatType) => string[];
  parseNumericShortcut?: (value: string) => ParsedDateResult;
}

export type ParsedDateResult =
  | {
      year: string;
      month: string;
      day: string;
      hour?: string;
      minute?: string;
    }
  | undefined;

interface InitScope {
  defaultTimezoneAwaiter: DeferredPromise<void>;
  disposables: Disposable[];
  isDisposed?: boolean;
  userSessionDependency: ko.Observable<unknown>;
}

let latestInitScope: InitScope | undefined;

export async function initAsync(): Promise<void> {
  const initScope: InitScope = {
    defaultTimezoneAwaiter: new DeferredPromise(),
    disposables: [],
    userSessionDependency: ko.observable(),
  };
  latestInitScope = initScope;

  const numberFormat = getNumberFormat();
  numericLocale.decimalCharacter = numberFormat.decimalSeparator;
  numericLocale.digitGroupSeparator = numberFormat.groupSeparator;
  numericLocale.digitalGroupSpacing = numberFormat.digitGrouping;

  await entityMappingService.waitOnLoaded();
  await ruleRepository.loadEntityAsync("IGlowMacro");

  const regionalSettings = global.portalInfo.regionalSettings;

  let numberFormatReported = false;
  const numberFormatComputed = setLocaleSettings(
    initScope,
    [
      regionalSettings?.numberGroupSizes,
      regionalSettings?.numberDecimalSeparator,
      regionalSettings?.numberGroupSeparator,
    ],
    ([numberGroupSizes, numberDecimalSeparator, numberGroupSeparator]: Array<string | undefined>) => {
      const groupSizeValue = numberGroupSizes || numberFormat.digitGrouping;
      const decimalSeparatorValue = numberDecimalSeparator || numberFormat.decimalSeparator;
      const groupSeparatorValue = numberGroupSeparator || numberFormat.groupSeparator;

      if (!validateNumberFormats(groupSizeValue, decimalSeparatorValue, groupSeparatorValue)) {
        numericLocale.decimalCharacter = numberFormat.decimalSeparator;
        numericLocale.digitGroupSeparator = numberFormat.groupSeparator;
        numericLocale.digitalGroupSpacing = numberFormat.digitGrouping;

        if (!numberFormatReported) {
          numberFormatReported = true;
          toastService.showToastAlertAsync(
            captionService.getString(
              "5f5b07c3-ca33-4873-b49b-81558b8301f1",
              "Number format settings for the currently logged-in company are not compatible with GLOW. Default formats will be used. Please contact your system administrator.",
            ),
            NotificationType.Error,
            false,
          );
        }
      } else {
        numericLocale.decimalCharacter = decimalSeparatorValue;
        numericLocale.digitGroupSeparator = groupSeparatorValue;
        numericLocale.digitalGroupSpacing = groupSizeValue as DigitalGroupSpacingOption;
      }
    },
  );

  const defaultTimezoneComputed = setLocaleSettings(
    initScope,
    [regionalSettings?.defaultTimeZone],
    ([newValue]: Array<string | undefined>) => {
      timeInfo.defaultTimeZone = newValue;
    },
    () => {
      initScope.defaultTimezoneAwaiter.resolve();
    },
  );

  const dateTimeFormatConfigComputed = setLocaleSettings(
    initScope,
    [regionalSettings?.dateTimeFormat],
    ([newValue]: Array<LocaleDateTimeFormatConfig | undefined>) => {
      timeInfo.dateTimeFormatConfig = newValue;
    },
  );

  const isLoggedOnSubscriber = userSession.onIsLoggedOnChanged((isLoggedOn: boolean) => {
    if (isLoggedOn && !initScope.isDisposed) {
      numberFormatReported = false;
      initScope.defaultTimezoneAwaiter = new DeferredPromise();
      initScope.userSessionDependency.notifySubscribers();
    }
  });

  initScope.disposables.push(
    numberFormatComputed,
    defaultTimezoneComputed,
    dateTimeFormatConfigComputed,
    isLoggedOnSubscriber,
  );
}

export async function ensureDefaultTimezoneInitializedAsync(): Promise<void> {
  await latestInitScope?.defaultTimezoneAwaiter.promise;
}

export function resetLocaleSettings(): void {
  if (latestInitScope) {
    latestInitScope.isDisposed = true;
    latestInitScope.disposables.forEach((d) => d.dispose());
    latestInitScope.defaultTimezoneAwaiter.resolve();
    latestInitScope = undefined;
  }

  Object.assign(timeInfo, defaultTimeInfo);
  Object.assign(numericLocale, defaultNumericLocale);
}

function setLocaleSettings<T>(
  initScope: InitScope,
  paths: Array<string | undefined>,
  callback: (newValue: Array<T | undefined>) => void,
  finallyCallback?: () => void,
): Disposable {
  let latestVersion = 0;

  return ko.computed(() => {
    initScope.userSessionDependency();
    loadValuesAsync(++latestVersion);
  });

  async function loadValuesAsync(version: number): Promise<void> {
    try {
      const results = await Promise.all(
        paths.map((path) => (path ? getDependencyValueAsync<T>({}, path, { throwOnError: true }) : undefined)),
      );

      if (version === latestVersion && !initScope.isDisposed) {
        const values = results.map((result) => result?.value ?? undefined);
        callback(values);
      }
    } finally {
      if (version === latestVersion && !initScope.isDisposed) {
        finallyCallback?.();
      }
    }
  }
}

function validateNumberFormats(
  groupSizeValue: string,
  decimalSeparatorValue: string,
  groupSeparatorValue: string | undefined,
): boolean {
  return (
    isValidDigitalGroupSpacingOption(groupSizeValue) &&
    validDecimalSeparators.includes(decimalSeparatorValue) &&
    (!groupSeparatorValue || validGroupSeparators.includes(groupSeparatorValue)) &&
    decimalSeparatorValue !== groupSeparatorValue
  );
}

const validGroupSeparators = [",", ".", "٬", "˙", "'", "\u0092", " ", "\u2009", "\u202f", "\u00a0", ""];
const validDecimalSeparators = [".", ",", "·", "⎖", "٫"];
